import React, { useEffect, useState } from "react";
import CodeEditorWindow from "./CodeEditorWindow";
import axios from "axios";
import { classnames } from "../utils/general";
import { languageOptions } from "../constants/languageOptions";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { defineTheme } from "../lib/defineTheme";
import useKeyPress from "../hooks/useKeyPress";
import OutputWindow from "./OutputWindow";
import CustomInput from "./CustomInput";
import OutputDetails from "./OutputDetails";
import ThemeDropdown from "./ThemeDropdown";
import LanguagesDropdown from "./LanguagesDropDown";
import NavigationBar from "./Navbar";

const javascriptDefault =
  `// Write your C++ code here 
#include <iostream>

int main() {
    std::cout << "Hello World!";
    return 0;
}`;

const Landing = () => {
  const [code, setCode] = useState(javascriptDefault);
  const [customInput, setCustomInput] = useState("");
  const [outputDetails, setOutputDetails] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [theme, setTheme] = useState("cobalt");
  const [language, setLanguage] = useState(languageOptions[5]);
  const [isMobileView, setIsMobileView] = useState(false);
  const [activePanel, setActivePanel] = useState("code"); // 'code', 'input', 'output'

  const enterPress = useKeyPress("Enter");
  const ctrlPress = useKeyPress("Control");

  // Handle responsive view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onSelectChange = (sl) => {
    console.log("selected Option...", sl);
    setLanguage(sl);
  };

  useEffect(() => {
    if (enterPress && ctrlPress) {
      console.log("enterPress", enterPress);
      console.log("ctrlPress", ctrlPress);
      handleCompile();
    }
  }, [ctrlPress, enterPress]);

  const onChange = (action, data) => {
    switch (action) {
      case "code": {
        setCode(data);
        break;
      }
      default: {
        console.warn("case not handled!", action, data);
      }
    }
  };

  const handleCompile = () => {
    setProcessing(true);
    const formData = {
      language_id: language.id,
      source_code: btoa(code),
      stdin: btoa(customInput),
    };
    const options = {
      method: "POST",
      url: "https://judge0-ce.p.rapidapi.com/submissions",
      params: { base64_encoded: "true", fields: "*" },
      headers: {
        "content-type": "application/json",
        "Content-Type": "application/json",
        "X-RapidAPI-Host": "judge0-ce.p.rapidapi.com",
        "X-RapidAPI-Key":
          "26f397d828msh57887dad54691fap13b5e9jsnd60485118279",
      },
      data: formData,
    };

    axios
      .request(options)
      .then(function (response) {
        console.log("res.data", response.data);
        const token = response.data.token;
        checkStatus(token);
      })
      .catch((err) => {
        let error = err.response ? err.response.data : err;
        let status = err.response.status;
        console.log("status", status);
        if (status === 429) {
          console.log("too many requests", status);
          showErrorToast(
            `Quota of 100 requests exceeded for the Day! Please read the blog on freeCodeCamp to learn how to setup your own RAPID API Judge0!`,
            10000
          );
        }
        setProcessing(false);
        console.log("catch block...", error);
      });
  };

  const checkStatus = async (token) => {
    const options = {
      method: "GET",
      url: "https://judge0-ce.p.rapidapi.com/submissions" + "/" + token,
      params: { base64_encoded: "true", fields: "*" },
      headers: {
        "X-RapidAPI-Host": "judge0-ce.p.rapidapi.com",
        "X-RapidAPI-Key": "eb0d3364bemsh928cad1305d8905p13986bjsn7357710f69cd",
      },
    };
    try {
      let response = await axios.request(options);
      let statusId = response.data.status?.id;

      if (statusId === 1 || statusId === 2) {
        setTimeout(() => {
          checkStatus(token);
        }, 2000);
        return;
      } else {
        setProcessing(false);
        setOutputDetails(response.data);
        showSuccessToast(`Compiled Successfully!`);
        console.log("response.data", response.data);
        return;
      }
    } catch (err) {
      console.log("err", err);
      setProcessing(false);
      showErrorToast();
    }
  };

  function handleThemeChange(th) {
    const theme = th;
    console.log("theme...", theme);

    if (["light", "vs-dark"].includes(theme.value)) {
      setTheme(theme);
    } else {
      defineTheme(theme.value).then((_) => setTheme(theme));
    }
  }

  useEffect(() => {
    defineTheme("oceanic-next").then((_) =>
      setTheme({ value: "oceanic-next", label: "Oceanic Next" })
    );
  }, []);

  const showSuccessToast = (msg) => {
    toast.success(msg || `Compiled Successfully!`, {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      style: {
        backgroundColor: '#1e2936',
        color: '#e5e7eb',
        border: '1px solid #10b981'
      }
    });
  };

  const showErrorToast = (msg, timer) => {
    toast.error(msg || `Something went wrong! Please try again.`, {
      position: "top-right",
      autoClose: timer ? timer : 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      style: {
        backgroundColor: '#1e2936',
        color: '#e5e7eb',
        border: '1px solid #ef4444'
      }
    });
  };

  // Mobile Panel Navigation
  const MobilePanelNav = () => (
    <div className="flex bg-[#1e2936] border-b border-gray-700/30">
      <button
        onClick={() => setActivePanel("code")}
        className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
          activePanel === "code"
            ? "bg-blue-600 text-white"
            : "text-gray-400 hover:text-gray-300"
        }`}
      >
        Code
      </button>
      <button
        onClick={() => setActivePanel("input")}
        className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
          activePanel === "input"
            ? "bg-blue-600 text-white"
            : "text-gray-400 hover:text-gray-300"
        }`}
      >
        Input
      </button>
      <button
        onClick={() => setActivePanel("output")}
        className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
          activePanel === "output"
            ? "bg-blue-600 text-white"
            : "text-gray-400 hover:text-gray-300"
        }`}
      >
        Output
      </button>
    </div>
  );

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        toastStyle={{
          backgroundColor: '#1e2936',
          color: '#e5e7eb'
        }}
      />

      <div className="min-h-screen bg-[#131723]">
        <NavigationBar/>
        
        {/* Header */}
        <div className="border-b border-gray-700/30 bg-[#131723] mt-18 sm:mt-20">
          <div className="px-3 sm:px-6 py-4">
            {/* Mobile Header */}
            <div className="block md:hidden space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 bg-clip-text text-transparent">
                  Vivi Code
                </h1>
                <button
                  onClick={handleCompile}
                  disabled={!code || processing}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-blue-500/25 text-sm"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  <span>{processing ? "Running..." : "Run"}</span>
                </button>
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex-1 min-w-0">
                  <LanguagesDropdown onSelectChange={onSelectChange} />
                </div>
                <div className="flex-1 min-w-0">
                  <ThemeDropdown handleThemeChange={handleThemeChange} theme={theme} />
                </div>
              </div>
            </div>

            {/* Desktop Header */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 bg-clip-text text-transparent">
                  Vivi Code
                </h1>
                <div className="flex items-center space-x-4">
                  <div className="min-w-[180px]">
                    <LanguagesDropdown onSelectChange={onSelectChange} />
                  </div>
                  <div className="min-w-[160px]">
                    <ThemeDropdown handleThemeChange={handleThemeChange} theme={theme} />
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-400 hidden lg:block">Ctrl + Enter to run</span>
                <button
                  onClick={handleCompile}
                  disabled={!code || processing}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-blue-500/25"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  <span>{processing ? "Running..." : "Run Code"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {isMobileView ? (
          // Mobile Layout
          <div className="flex flex-col h-[calc(100vh-120px)] sm:h-[calc(100vh-140px)]">
            <MobilePanelNav />
            
            <div className="flex-1 overflow-hidden">
              {activePanel === "code" && (
                <div className="h-full p-2 sm:p-4">
                  <div className="h-full bg-[#1e2936] rounded-lg border border-gray-700/30 overflow-hidden shadow-xl">
                    <CodeEditorWindow
                      code={code}
                      onChange={onChange}
                      language={language?.value}
                      theme={theme.value}
                    />
                  </div>
                </div>
              )}
              
              {activePanel === "input" && (
                <div className="h-full p-2 sm:p-4">
                  <div className="h-full flex flex-col space-y-3">
                    <div className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                      </svg>
                      <h3 className="text-sm font-medium text-gray-300">Input</h3>
                    </div>
                    <div className="flex-1 bg-[#1e2936] rounded-lg border border-gray-700/30 overflow-hidden">
                      <CustomInput
                        customInput={customInput}
                        setCustomInput={setCustomInput}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {activePanel === "output" && (
                <div className="h-full p-2 sm:p-4">
                  <div className="h-full flex flex-col space-y-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <h3 className="text-sm font-medium text-gray-300">Output</h3>
                      {processing && (
                        <div className="flex items-center space-x-2">
                          <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></div>
                          <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 bg-[#1e2936] rounded-lg border border-gray-700/30 overflow-hidden shadow-lg">
                      <OutputWindow outputDetails={outputDetails} />
                    </div>

                    {outputDetails && (
                      <div className="rounded-lg border border-gray-700/30 p-4 shadow-lg">
                        <OutputDetails outputDetails={outputDetails} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Desktop Layout
          <div className="flex h-[calc(100vh-73px)]">
            {/* Left Panel - Code Editor */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex-1 p-4">
                <div className="h-full bg-[#1e2936] rounded-lg border border-gray-700/30 overflow-hidden shadow-xl">
                  <CodeEditorWindow
                    code={code}
                    onChange={onChange}
                    language={language?.value}
                    theme={theme.value}
                  />
                </div>
              </div>
            </div>

            {/* Right Panel - Output & Input */}
            <div className="w-80 xl:w-96 flex flex-col border-l border-gray-700/30 bg-[#131723] min-w-0">
              {/* Input Section */}
              <div className="p-4 border-b border-gray-700/30 flex-shrink-0">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                    </svg>
                    <h3 className="text-sm font-medium text-gray-300">Input</h3>
                  </div>
                  <div className="bg-[#1e2936] rounded-lg border border-gray-700/30 h-32">
                    <CustomInput
                      customInput={customInput}
                      setCustomInput={setCustomInput}
                    />
                  </div>
                </div>
              </div>

              {/* Output Section */}
              <div className="flex-1 p-4 min-h-0">
                <div className="h-full flex flex-col space-y-4">
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <h3 className="text-sm font-medium text-gray-300">Output</h3>
                    {processing && (
                      <div className="flex items-center space-x-2">
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></div>
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 bg-[#1e2936] rounded-lg border border-gray-700/30 overflow-hidden shadow-lg min-h-0">
                    <OutputWindow outputDetails={outputDetails} />
                  </div>

                  {outputDetails && (
                    <div className="rounded-lg border border-gray-700/30 p-4 shadow-lg flex-shrink-0">
                      <OutputDetails outputDetails={outputDetails} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Landing;